import React, { useState, useCallback, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
// Mightier Survey Engine
import { TextQuestion } from "@neuromotion-inc/mightier-survey-engine";
import { SurveyEngineIface } from "src/services/surveyHooks/useSurveyEngine";
// Styling
import { TextInput, Container } from "@mantine/core";
import css from "./survey-text-input.module.css";

interface SurveyTextInputProps {
  question: TextQuestion;
  surveyEngine: SurveyEngineIface;
  questionIdToValidationErrorsMap: Map<string, string[]>;
  testId: string;
}

export const SurveyTextInput = React.memo<SurveyTextInputProps>(
  function SurveyTextInput({
    question,
    surveyEngine,
    questionIdToValidationErrorsMap,
    testId,
  }) {
    const { t } = useTranslation("translation");

    const [touched, setTouched] = useState(false); // Track if the input has been interacted with

    const { errors, showError } = useMemo(() => {
      const errors = questionIdToValidationErrorsMap.get(question.id) ?? [];
      const showError = touched && errors.length > 0;
      return { errors, showError };
    }, [touched, question.id, questionIdToValidationErrorsMap]);

    const currAnswer = useMemo(() => {
      const response = surveyEngine.getResponseForQuestion(question.id);
      // if the user has not answered yet
      if (!response) {
        return "";
      }
      if (response.type !== "TEXT") {
        Sentry.captureMessage("Response type mismatch in SurveyTextInput");
        return "";
      }

      return response.responseValue;
    }, [surveyEngine, question.id]);

    const onChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setTouched(true);

        surveyEngine.recordResponse({
          question_id: question.id,
          responseValue: e.currentTarget.value,
          type: "TEXT",
        });
      },
      [surveyEngine, question.id],
    );

    return (
      <Container>
        <TextInput
          size="md"
          styles={{
            input: {
              fontSize: "21px", // Override Mantine's default font size
            },
          }}
          className={css["text-input"]}
          variant="unstyled"
          placeholder={t("survey.typeAnswer")}
          onBlur={onChange}
          value={currAnswer}
          onChange={onChange}
          error={showError ? errors.join(", ") : null} // Show error only if touched
          data-testid={testId}
        />
      </Container>
    );
  },
);
